import { firebase, storage } from 'core';
import { Api } from 'lib';
import { useEffect, useRef } from 'react';
import { store } from 'store';

const APPLE_SDK_URL =
  'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

function AppleLogin({ CustomButton, followUpDetails, redirectUrl }) {
  const initializedRef = useRef(false);

  useEffect(() => {
    if (document.getElementById('apple-js-sdk')) {
      initApple();
      return;
    }
    const script = document.createElement('script');
    script.id = 'apple-js-sdk';
    script.src = APPLE_SDK_URL;
    script.onload = initApple;
    document.head.appendChild(script);
  }, []);

  function initApple() {
    if (initializedRef.current || !window.AppleID) return;
    initializedRef.current = true;
    window.AppleID.auth.init({
      clientId: process.env.REACT_APP_APPLE_CLIENT_ID,
      scope: 'name email',
      redirectURI: process.env.REACT_APP_APPLE_REDIRECT_URI,
      usePopup: true,
    });
  }

  async function login() {
    try {
      // Ensure SDK is ready (handles the case where user clicks before onload fires)
      if (!initializedRef.current) initApple();
      if (!window.AppleID) return;

      const data = await window.AppleID.auth.signIn();

      const identityToken = data?.authorization?.id_token;
      const authorizationCode = data?.authorization?.code;

      // `data.user` is only present on the very first sign-in for a given user
      const appleUser = data?.user;
      const name = appleUser
        ? `${appleUser.name?.firstName || ''} ${appleUser.name?.lastName || ''}`.trim()
        : undefined;

      const res = await Api.post(
        '/users/appleLogin',
        { identityToken, authorizationCode, user: { name } },
        'navigator'
      );
      if (!res?.user) return;

      await storage.setItem('token', res.token);

      firebase.logEvent(res.created ? 'sign_up' : 'login', { method: 'apple' });

      if (followUpDetails && Object.keys(followUpDetails).length > 0) {
        const { actionType, otherUser, action } = followUpDetails;
        const isShoutout = actionType === 'requestShoutout' || actionType === 'sendShoutout';

        store.dispatch({
          type: 'SET_USER',
          payload: {
            ...res.user,
            atheleteIdToSubscribe: actionType === 'subscribe' ? otherUser?._id : null,
            athleteUsernameToFollow: actionType === 'follow' ? otherUser?.username : null,
            atheleteUsernameToFace2Face: actionType === 'face2face' ? otherUser?.username : null,
            atheleteUsernameToAdPlayaz: actionType === 'adPlayaz' ? otherUser?.username : null,
            athleteUsernameToShoutout: isShoutout ? otherUser?.username : null,
            fromAtheleteUsername: actionType === 'fromUser' ? otherUser?.username : null,
            followUpAction: actionType,
          },
        });

        if (action && typeof action === 'function') {
          await action();
        }
      } else {
        const navigateTo = redirectUrl
          ? decodeURIComponent(redirectUrl).replace(/^\//, '')
          : res.user.admin || process.env.REACT_APP_DEFAULT_TEAM_REDIRECT === 'false'
          ? null
          : `profile/${process.env.REACT_APP_DEFAULT_TEAM_USERNAME || 'fctokyo'}`;

        store.dispatch({
          type: 'SET_USER',
          payload: { ...res.user, navigateTo },
        });
      }
    } catch (err) {
      // User dismissed the popup — not an error
      if (err?.error === 'popup_closed_by_user') return;
      console.error('Apple Sign-In error', err);
    }
  }

  return <CustomButton onClick={login} />;
}

export default AppleLogin;
